// FLEET · COMMISSIONS and the commission card (v2-the-deep.md §10, §18.1,
// R2629, R2634, R2645; mockup round 088 card 15 (a)).
//
// The pill stands only while the flag `commissions` holds (`FLAG_SUBS`).
// Its body has four sections (R2701, R2715). PENDING stands first, in the
// amber ink: the offers whose task is done and whose ship waits for room
// in the Dock, each with the line that says what frees room. Then YOUR
// COMMISSIONS, the offers a fleet of this commander works, then THE
// BOARD, the open offers of the faction, then the ARCHIVE of
// `Empire.commissions_paid`, the commissions that paid this commander
// this season, newest first. The ARCHIVE stands folded, as FOUND of
// TREASURE does: its head counts the records, and SHOW opens it. One 44 px
// build row each: the commission mark,
// the kind, the task line with the time left, the pay in the cost column,
// and OPEN. OPEN and the name open the commission card in the context slot
// (a page of the menu sheet on a telephone), because the card is a build
// card of the kind `commission` (`infocards.ts`).
//
// The card lists the own fleets at sea. A fleet that fits takes an ASSIGN
// press: one fleet for each press, and the press orders the fleet nowhere
// (R1307). A fleet that does not fit states why. The card reads the facts
// the wire carries for the three checks of the hub (`commissionFits`): the
// depth rating of the hull against the band of a trench (`ratingOf`, R2637), the gun of the
// class for a den, and a captain on a hull with a lead attribute for a pay
// of practice. The hub judges the press, and a refusal arrives as a
// notice. SHOW ON MAP centres the map on the target cell.
//
// The two caps of the board (v2-the-deep.md §10, R3065, R3069): the head
// of THE BOARD states the takes of the world day against the cap of the
// day (`Empire.commission_takes_today`), in the amber ink at the cap. A
// notice under the head says when the commander holds the most offers at
// one time. At either cap the card turns ASSIGN off, as on an offer whose
// deadline passed, and its note says why. Both caps come from
// `Hello.catalog.rules`; a cap of 0 (no hello yet, or the flag off) shows
// nothing.
//
// The times of the board (R3071): the head stands on every state of the
// board, so the counter does too. A board with no open offer states when
// the hourly issue refills it and when the count of the day resets, and
// the counter at the cap states the reset. The client reads both times
// off the world clock it holds (`boardTimes`), and the 4 Hz regenerate of
// the panel counts them down between frames.
//
// The page is pure: it reads the state and writes markup.

import {
  CommissionKind, CommissionReward, type Commission, type CommissionPaid, type ShipClass,
} from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { type GameState, costText, dur, num } from '../../state';
import { bandAt } from '../../deep';
import { SEC_PER_DAY, SEC_PER_HOUR, secOfDay } from '../../locale/clock';
import { cellCenter } from '../../world';
import { bandWord } from '../chart/deep-legend';
import { buttonHtml, type CardSpec, type Fact } from '../cards/card';
import { ratingOf } from '../cards/mapcards';
import { esc } from '../esc';
import { cssColor } from '../format';
import { buildRowHtml } from './buildrow';
import { secHead } from './econwar';

/** One own fleet at sea, as the card reads it. `UIFleet` satisfies it. */
export interface CommissionFleet {
  id: number;
  name: string;
  cls: ShipClass;
  captainId: number;
  x: number;
  y: number;
  /** The depth rating of the hull that the server sends, which counts
   *  R4 Maintenance Corps (R2637); absent or 0 reads the class rating. */
  depthRating?: number;
}

/** The info kind of the card (`infocards.ts` `InfoKind`). */
export const COMMISSION_INFO = 'commission';

/** The act of the fold of the ARCHIVE (R2715). */
export const COMMISSION_ARCHIVE = 'comarchive';

/** How many fleets that do not fit the card names with their reason. The
 *  rest stand as one count line (round 088 card 15 (a)). */
const UNFIT_ROWS = 2;

/**
 * The commission mark (round 088 card 15): a pennant on a staff, in the
 * own faction ink. It is not a map marker look (§14.6), so every look of
 * `mapMarker` keeps it; the map draws the same shape (`render/commission-mark.ts`).
 */
export function commissionMarkSvg(ink: string, cls = 'ck-mark'): string {
  return `<svg class="${cls}" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"`
    + ` style="color:${ink}"><path d="M7 21V3" fill="none" stroke="currentColor" stroke-width="1.7"`
    + ' stroke-linecap="round"/><path d="M7 3.5l12 4-12 4z" fill="currentColor"/></svg>';
}

/** The name of a kind: what the faction asks for. */
export function commissionTitle(kind: CommissionKind): string {
  switch (kind) {
    case CommissionKind.COMMISSION_KIND_SURVEY: return S.com_kind_survey;
    case CommissionKind.COMMISSION_KIND_SOUND: return S.com_kind_sound;
    case CommissionKind.COMMISSION_KIND_DEN: return S.com_kind_den;
    case CommissionKind.COMMISSION_KIND_CACHE: return S.com_kind_cache;
  }
  return S.com_kind_other;
}

/** The task of a kind, as one sentence of the card. */
function taskText(kind: CommissionKind): string {
  switch (kind) {
    case CommissionKind.COMMISSION_KIND_SURVEY: return S.com_task_survey;
    case CommissionKind.COMMISSION_KIND_SOUND: return S.com_task_sound;
    case CommissionKind.COMMISSION_KIND_DEN: return S.com_task_den;
    case CommissionKind.COMMISSION_KIND_CACHE: return S.com_task_cache;
  }
  return '';
}

/** The pay of an offer, as markup: the resource bundle with its icons, the
 *  rounds, the practice or the hull. */
export function commissionPay(s: GameState, c: Commission): string {
  switch (c.reward) {
    case CommissionReward.COMMISSION_REWARD_RESOURCES: return costText(c.resources);
    case CommissionReward.COMMISSION_REWARD_ROUNDS:
      return esc(S.com_pay_rounds({ n: c.rounds, name: s.torpedo(c.roundKind)?.name ?? S.com_pay_round_any }));
    case CommissionReward.COMMISSION_REWARD_PRACTICE:
      return esc(S.com_pay_practice({ n: Number.isInteger(c.practice) ? String(c.practice) : c.practice.toFixed(1) }));
    case CommissionReward.COMMISSION_REWARD_SHIP:
      return esc(S.com_pay_ship({ name: shipName(s, c) }));
  }
  return '—';
}

/** Is the offer one this commander took? A taken offer names its fleet. */
const isTaken = (c: Commission): boolean => c.fleetId !== 0;

/** Is the offer one a fleet still works: taken, and its task not done? */
const isWorked = (c: Commission): boolean => isTaken(c) && !c.done;

/** The offers this commander holds: the taken ones, pending ones too, as
 *  the hub counts them against the hold cap (`TakeCommission`). */
const heldCount = (s: GameState): number => (s.empire?.commissions ?? []).filter(isTaken).length;

/** The takes of the world day, and whether they reached the cap of the
 *  day (R3069). A cap of 0 reads never full. */
function dayTakes(s: GameState): { n: number; max: number; full: boolean } {
  const n = s.empire?.commissionTakesToday ?? 0;
  const max = s.rules.commissionDayCap;
  return { n, max, full: max > 0 && n >= max };
}

/** Does the commander hold the most offers at one time (R3069)? */
function holdFull(s: GameState): boolean {
  const max = s.rules.commissionHoldCap;
  return max > 0 && heldCount(s) >= max;
}

/** The sentence that says why ASSIGN is off at a cap, or ''. The cap of
 *  the day comes first, as the hub checks it first (R3065). */
function capWhy(s: GameState): string {
  const day = dayTakes(s);
  if (day.full) return S.com_day_full({ n: day.max });
  if (holdFull(s)) return S.com_hold_full({ n: s.rules.commissionHoldCap });
  return '';
}

/**
 * The world seconds until the next refill of the board and until the next
 * turn of the world day (R3071). The hub counts both on the world clock of
 * the season (`gameElapsed`): `issueCommissions` refills the board at each
 * whole world hour of it, and the count of the day resets at each whole
 * world day. The time of day is that clock plus the origin of the season
 * (R2519), so the clock less the origin is the place of the season in its
 * world day. The hour and the day are facts of the clock, no pack figure.
 */
export function boardTimes(s: GameState): { refillSec: number; resetSec: number } {
  const ofDay = secOfDay(s.worldClockSec() - s.clockOriginSec);
  return { refillSec: SEC_PER_HOUR - (ofDay % SEC_PER_HOUR), resetSec: SEC_PER_DAY - ofDay };
}

/** The head of THE BOARD: the count of the open offers, and at the right
 *  the takes of the world day against the cap of the day, in the amber
 *  ink at the cap with the time to the reset (R3069, R3071). No cap on
 *  the wire keeps the plain head. */
function boardHead(s: GameState, open: number): string {
  const title = S.com_head_open({ n: open });
  const day = dayTakes(s);
  if (day.max === 0) return `<h3>${title}</h3>`;
  const text = esc(day.full
    ? S.com_today_full({ n: day.n, max: day.max, left: dur(boardTimes(s).resetSec) })
    : S.com_today({ n: day.n, max: day.max }));
  const tip = esc(S.com_today_title({ max: day.max }));
  return secHead(title, `<span class="com-today${day.full ? ' ink-amber' : ''}" title="${tip}">${text}</span>`);
}

/** The line of a board with no open offer (R3071): when the board refills,
 *  and when the count of the day resets. No cap on the wire states no
 *  count. */
function boardEmpty(s: GameState): string {
  const t = boardTimes(s);
  const lines = [S.com_board_none, S.com_board_refill({ left: dur(t.refillSec) })];
  if (s.rules.commissionDayCap > 0) lines.push(S.com_count_resets({ left: dur(t.resetSec) }));
  return `<div class="empty com-times">${esc(lines.join(' '))}</div>`;
}

/** The own fleet with this id, or undefined. */
const fleetOf = (fleets: readonly CommissionFleet[], id: number) => fleets.find((f) => f.id === id);

/**
 * Why a fleet does not fit an offer, or '' when it fits. The reasons are
 * the checks of the hub that the wire carries the facts of, plus the rule
 * that one fleet works one offer. The hub judges the press.
 */
export function unfitWhy(s: GameState, c: Commission, f: CommissionFleet): string {
  if ((s.empire?.commissions ?? []).some((o) => o.fleetId === f.id)) return S.com_unfit_busy;
  const spec = s.ship(f.cls);
  if (c.kind === CommissionKind.COMMISSION_KIND_SOUND && s.depthBands) {
    const rating = ratingOf(f, spec);
    if (rating !== 0 && rating < bandAt(s.depthBands, c.cell)) {
      return S.com_unfit_rating({ band: bandWord(rating, false) });
    }
  }
  if (c.kind === CommissionKind.COMMISSION_KIND_DEN && spec?.unarmed) return S.com_unfit_gun;
  if (c.reward === CommissionReward.COMMISSION_REWARD_PRACTICE && (f.captainId === 0 || (spec?.leadAttr ?? -1) < 0)) {
    return S.com_unfit_captain;
  }
  return '';
}

/** The distance from a fleet to the centre of the target cell, world px. */
function pxTo(c: Commission, f: CommissionFleet): number {
  const [x, y] = cellCenter(c.cell);
  return Math.hypot(f.x - x, f.y - y);
}

/** The line of the time left: the clock, or the word of a pending offer. */
function leftText(c: Commission): string {
  return c.done ? S.com_pending_short : S.com_left({ left: dur(c.leftSec) });
}

/** The name of the hull a ship pay lays down. */
const shipName = (s: GameState, c: Commission): string => s.ship(c.ship)?.name ?? S.com_pay_ship_any;

/** The OPEN press of a row, and the name: both open the card. */
function infoButton(c: Commission, cls: string, inner: string, title: string): string {
  return `<button class="${cls}" data-act="info" data-kind="${COMMISSION_INFO}" data-id="${c.id}"`
    + ` title="${esc(title)}">${inner}</button>`;
}

/** One row of the pill. */
function commissionRow(s: GameState, c: Commission, fleets: readonly CommissionFleet[]): string {
  const taken = isTaken(c);
  const fleet = taken ? fleetOf(fleets, c.fleetId)?.name ?? S.com_fleet_gone : '';
  // A pending offer reads PENDING, never TAKEN or DONE (R2701, R2715), and
  // its line says in the amber ink what the pay waits for and what frees
  // room in the Dock.
  const line2 = c.done
    ? `<span class="ink-amber">${esc(S.com_pending_line({ name: shipName(s, c) }))}</span>`
    : taken ? `${esc(fleet)} · ${esc(leftText(c))}` : esc(leftText(c));
  const press = c.done
    ? infoButton(c, 'bp pending', `<span class="w">${S.com_pending}</span>`, S.com_info_title)
    : taken
      ? infoButton(c, 'bp held', `<span class="w">${S.com_taken}</span>`, S.com_info_title)
      : infoButton(c, 'bp ready', `<span class="w">${S.com_open}</span>`, S.com_open_title);
  return buildRowHtml({
    icon: commissionMarkSvg(c.done ? 'var(--amber)' : cssColor(s.faction), 'bic'),
    name: infoButton(c, 'info', esc(commissionTitle(c.kind)), S.com_info_title),
    line2,
    side: `<span class="cost">${commissionPay(s, c)}</span>`,
    press,
    cls: c.done ? 'pending' : taken ? 'taken' : '',
    wrap: c.done,
  });
}

/** The world day of the season of a world second, from 1. */
const seasonDay = (sec: number): number => Math.floor(sec / SEC_PER_DAY) + 1;

/** One row of the ARCHIVE (R2715): the kind, the fleet and the day of the
 *  pay, the pay, and the solid word PAID. A record opens no card, because
 *  the offer left the board and the empire. */
function paidRow(s: GameState, r: CommissionPaid): string {
  const c = r.offer;
  if (!c) return '';
  return buildRowHtml({
    icon: commissionMarkSvg('var(--dim)', 'bic'),
    name: `<span class="name">${esc(commissionTitle(c.kind))}</span>`,
    line2: esc(S.com_paid_line({ fleet: r.fleetName || S.com_fleet_gone, day: seasonDay(r.paidSec) })),
    side: `<span class="cost">${commissionPay(s, c)}</span>`,
    press: `<button class="bp done" disabled title="${esc(S.com_paid_title)}"><span class="w">${S.com_paid}</span></button>`,
    cls: 'paid',
  });
}

/** The ARCHIVE section (R2715): folded by default, its head counts the
 *  records, and SHOW n / HIDE opens and folds it (the pattern of FOUND in
 *  ECONOMY · TREASURE). The newest pay stands first. */
function archiveHtml(s: GameState, open: boolean): string {
  const paid = s.empire?.commissionsPaid ?? [];
  let html = `<h3>${S.com_head_archive({ n: paid.length })}</h3>`;
  if (paid.length === 0) return html + `<div class="empty">${S.com_archive_empty}</div>`;
  if (open) html += [...paid].reverse().map((r) => paidRow(s, r)).join('');
  return html + `<div class="acts"><button data-act="${COMMISSION_ARCHIVE}" aria-expanded="${open}">`
    + `${open ? S.com_archive_hide : S.com_archive_show({ n: paid.length })}</button></div>`;
}

/** The page of FLEET · COMMISSIONS. `archiveOpen` unfolds the ARCHIVE. */
export function commissionsHtml(s: GameState, fleets: readonly CommissionFleet[], archiveOpen = false): string {
  const list = s.empire?.commissions ?? [];
  const worked = list.filter(isWorked);
  const pending = list.filter((c) => c.done);
  const open = list.filter((c) => !isTaken(c));
  const rows = (cs: readonly Commission[]) => cs.map((c) => commissionRow(s, c, fleets)).join('');
  let html = '';
  // PENDING first, in the amber ink (R2715): the pay of these offers
  // waits for the commander to free room in the Dock.
  if (pending.length > 0) {
    html += `<h3 class="ink-amber">${S.com_head_pending({ n: pending.length })}</h3>`
      + rows(pending);
  }
  if (worked.length > 0) html += `<h3>${S.com_head_taken({ n: worked.length })}</h3>` + rows(worked);
  html += boardHead(s, open.length)
    + (holdFull(s) ? `<div class="empty com-hold">${esc(S.com_hold_full({ n: s.rules.commissionHoldCap }))}</div>` : '')
    + (open.length > 0 ? rows(open) : boardEmpty(s));
  // THE BOARD stands on every state, so the counter does (R3071). The
  // hint explains OPEN and ASSIGN, so it waits for a first offer.
  html += archiveHtml(s, archiveOpen);
  return list.length === 0 ? html : html + `<div class="empty">${S.com_hint}</div>`;
}

/** One fleet row of the card: the fleet, its distance, and ASSIGN or why
 *  it does not fit. ASSIGN is off on an offer whose deadline passed: the
 *  server refuses it, and the refill of the next world hour clears it
 *  (v2-the-deep.md §10, R2939). It is off at a cap of the board too, the
 *  cap of the day or the hold cap (R3069). */
function fitRow(s: GameState, c: Commission, f: CommissionFleet, why: string, capped: boolean): string {
  const ink = why ? 'var(--dim)' : cssColor(s.faction);
  const cls = s.ship(f.cls)?.name ?? '';
  const sub = esc(S.com_fit_away({ px: num(pxTo(c, f)) }));
  const end = why
    ? `<span class="ck-rf"><small>${esc(why)}</small></span>`
    : buttonHtml({
      act: 'comassign', id: c.id, n: f.id, face: 'dark', label: S.com_assign,
      title: S.com_assign_title({ name: f.name }), off: c.leftSec <= 0 || capped,
    }, { keycaps: false });
  return `<div class="ck-row${why ? ' no' : ''}">${commissionMarkSvg(ink)}`
    + `<span class="ck-rn"><b style="color:${ink}">${esc(f.name)}</b><i>${esc(cls)} · ${sub}</i></span>${end}</div>`;
}

/** The fit list of an open offer: the fleets that fit, nearest first, then
 *  at most `UNFIT_ROWS` that do not, and a count of the rest. */
function fitListHtml(s: GameState, c: Commission, fleets: readonly CommissionFleet[]): string {
  if (fleets.length === 0) return `<div class="ck-sect"><p class="ck-desc">${esc(S.com_no_fleet)}</p></div>`;
  const rated = fleets.map((f) => ({ f, why: unfitWhy(s, c, f), px: pxTo(c, f) }))
    .sort((a, b) => a.px - b.px || a.f.id - b.f.id);
  const fit = rated.filter((r) => r.why === '');
  const unfit = rated.filter((r) => r.why !== '');
  const shown = unfit.slice(0, UNFIT_ROWS);
  const more = unfit.length - shown.length;
  const capped = capWhy(s) !== '';
  return '<div class="ck-sect"><div class="ck-sh ck-cap">'
    + `<span>${S.com_fit_head}</span><span class="ck-shr">${S.com_fit_count({ n: fit.length, total: fleets.length })}</span></div>`
    + `<div class="ck-list">${[...fit, ...shown].map((r) => fitRow(s, c, r.f, r.why, capped)).join('')}</div>`
    + (more > 0 ? `<p class="ck-desc">${esc(S.com_more_unfit({ n: more }))}</p>` : '')
    + '</div>';
}

/**
 * The commission card (round 088 card 15 (a)), or null when the offer left
 * the list: the card then closes, as a build card does when its row goes.
 */
export function commissionSpec(s: GameState, id: number, fleets: readonly CommissionFleet[]): CardSpec | null {
  const c = (s.empire?.commissions ?? []).find((x) => x.id === id);
  if (!c) return null;
  const taken = isTaken(c);
  const fleet = taken ? fleetOf(fleets, c.fleetId) : undefined;
  const facts: Fact[] = [
    { key: S.com_f_pays, val: commissionPay(s, c) },
    { key: S.com_f_left, val: esc(leftText(c)) },
  ];
  const band = bandAt(s.depthBands, c.cell);
  if (band > 0) facts.push({ key: S.com_f_band, val: esc(bandWord(band, false)) });
  if (taken) facts.push({ key: S.com_f_fleet, val: esc(fleet?.name ?? S.com_fleet_gone) });
  const note = c.done ? S.com_pending_wait({ name: shipName(s, c) })
    : taken ? S.com_note_taken({ name: fleet?.name ?? S.com_fleet_gone })
      : capWhy(s) || S.com_note;
  return {
    size: 'card',
    mark: commissionMarkSvg(cssColor(s.faction)),
    title: commissionTitle(c.kind),
    ink: { faction: s.faction },
    // A pending offer (R2715): the kind and the chip say PENDING in amber.
    kind: c.done ? S.com_card_kind_pending : taken ? S.com_card_kind_taken : S.com_card_kind_open,
    status: c.done ? { text: S.com_chip_pending, tone: 'amber' }
      : taken ? { text: S.com_chip_taken, tone: 'solid' } : { text: S.com_chip_open, tone: 'on' },
    facts,
    body: `<p class="ck-desc">${esc(taskText(c.kind))}</p>` + (taken ? '' : fitListHtml(s, c, fleets)),
    note: { lines: [esc(note)] },
    actions: [{ act: 'focuscell', n: c.cell, face: taken ? 'dark' : 'quiet', label: S.com_show }],
  };
}
